import React, { FC, useCallback, memo, useMemo } from 'react';
import _filter from 'lodash/filter';
import _sortBy from 'lodash/sortBy';
import { useLazyQuery } from '@apollo/client';
import DropdownWithChildren from '@minecraft.dropdown-with-children';
import ErrorMessage from '@minecraft.error-message';
import { gqlScheduleRequestType } from '@minecraft.graphql-types';
import { useTranslation } from '@blocs.i18n';
import { FilterData } from '../../../shared/types';
import { generateSelectOptionsBasedOnField } from '../../../shared/utils';
import { QUERY_SCHEDULES } from '../FilterBasicCollectionGraphQL';

type Props = {
  name: FilterData['name'];
  projectId: number;
  label: FilterData['label'];
};

const Schedules: FC<Props> = ({ name, projectId, label }) => {
  const { t } = useTranslation();

  const [getSchedules, { data, loading, error }] = useLazyQuery(QUERY_SCHEDULES, {
    variables: {
      projectId,
    },
  });

  const handleSchedulesFetch = useCallback((): void => {
    if (!data) {
      getSchedules();
    }
  }, [data]);

  const schedulesOptions = useMemo(() => {
    const auditionOptions = generateSelectOptionsBasedOnField(
      _sortBy(
        _filter(
          data?.searchSchedules?.schedules,
          (schedule) => !schedule.isCanceled && schedule.scheduleType === gqlScheduleRequestType.AUDITION
        ),
        'sortOrder'
      ),
      'name'
    );

    const callbackOptions = generateSelectOptionsBasedOnField(
      _sortBy(
        _filter(
          data?.searchSchedules?.schedules,
          (schedule) => !schedule.isCanceled && schedule.scheduleType !== gqlScheduleRequestType.AUDITION
        ),
        'sortOrder'
      ),
      'name'
    );

    return [
      ...(auditionOptions.length > 0
        ? [{ value: gqlScheduleRequestType.AUDITION, label: t('common:role.audition_other'), options: auditionOptions }]
        : []),
      ...(callbackOptions.length > 0
        ? [{ value: gqlScheduleRequestType.CALLBACK, label: t('common:role.callback_other'), options: callbackOptions }]
        : []),
    ];
  }, [data?.searchSchedules?.schedules]);

  if (error) return <ErrorMessage error={error} />;

  return (
    <DropdownWithChildren
      withFormik
      withSearch
      withSelectAll
      isMultipleSelect
      isLoading={loading}
      name={name}
      toggleLabel={label}
      options={schedulesOptions}
      onOptionsFetch={handleSchedulesFetch}
    />
  );
};

export default memo(Schedules);
